import { isEmpty } from "lodash-es";
import React, { useEffect, useRef, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import * as Yup from "yup";

import SubmitBtn from "../components/form/SubmitBtn";
import { isIframed } from "../helpers/utils";
import useGetServiceAreaByZip from "../hooks/useGetServiceAreaByZip";
import { useAutoIframeHeight, usePrevious } from "../hooks/utility";

const MARKETINGSITE = process.env.REACT_APP_MARKETING_SITE_URL;

const formSchema = Yup.object().shape({
  zip: Yup.string()
    .required("Zip code is required.")
    .length(5, "Zip code must be 5 digits long.")
    .matches(/(^\d{5}$)|(^\d{5}-\d{4}$)/, "Zip code entered is invalid."),
});

const SearchServiceArea = () => {
  const [zip, setZip] = useState("");
  const [validationError, setValidationError] = useState();
  const [validating, setValidating] = useState(false);
  const [hasService, setHasService] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const formRef = useRef();
  useAutoIframeHeight(formRef, "searchShipmentLocHeight");

  const { serviceArea, dispatchGetServiceAreaByZip, fetching, fetchingError } =
    useGetServiceAreaByZip(zip, "US");
  const prevFetching = usePrevious(fetching);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidating(true);
    setValidationError(null);
    try {
      await formSchema.validate({ zip });
      await dispatchGetServiceAreaByZip();
    } catch (error) {
      if (error.name && error.name === "ValidationError") {
        setValidationError(error.message);
      }
    }
    setValidating(false);
  };

  useEffect(() => {
    if (isIframed) {
      document.body.classList.add("bg-transparent");
      return () => {
        document.body.classList.remove("bg-transparent");
      };
    }
  }, []);

  useEffect(() => {
    if (prevFetching && !fetching) {
      if (!isEmpty(serviceArea)) {
        setZip(`${serviceArea["city"]}, ${serviceArea["state"]}`);
        setHasService(true);
      } else if (fetchingError) {
        setHasService(false);
      }
      setHasSearched(true);
    }
  }, [fetching, prevFetching, serviceArea, fetchingError]);

  const handleZipChange = (e) => {
    const zipVal = e.target.value;
    setZip(zipVal);
  };

  const handleGoToShipNow = (e) => {
    e.preventDefault();
    if (isIframed) {
      window.parent.postMessage({ redirect: "/ship-now" }, MARKETINGSITE);
    }
  };

  const handleCheckCoverage = (e) => {
    e.preventDefault();
    if (isIframed) {
      window.parent.postMessage({ redirect: "/news/lso-coverage" }, MARKETINGSITE);
    }
  };

  const handleCalcRates = (e) => {
    e.preventDefault();
    if (isIframed) {
      window.parent.postMessage({ redirect: "/calculate-rates" }, MARKETINGSITE);
    }
  };

  const InvalidZipMessage = (
    <div className="px-4">
      <p className="text-body-bigger font-weight-bold">
        Sorry, it doesn't look like we service that area at the moment.
      </p>
      <p className="text-body-bigger">
        Please click{" "}
        <a href="#check-coverage" onClick={handleCheckCoverage}>
          <u>here</u>
        </a>{" "}
        to see the areas we do cover.
      </p>
    </div>
  );

  const ValidZipMessage = (
    <div className="px-4">
      <p className="text-body-bigger font-weight-bold">
        Great news! We do provide shipping service to that area!
      </p>
      <p className="text-body-bigger">
        Click{" "}
        <a href="#calculate-rates" onClick={handleCalcRates}>
          <u>Calculate rates</u>
        </a>{" "}
        to check shipping rates or click{" "}
        <a href="#ship-now" onClick={handleGoToShipNow}>
          <u>Ship Now</u>
        </a>{" "}
        to start shipping.
      </p>
    </div>
  );

  return (
    <div className="bg-transparent text-white" ref={formRef}>
      <Form id="search-service-area" onSubmit={handleSubmit} className="mb-4">
        <InputGroup className="input-group-merge surround-btn-slant appended">
          <Form.Control
            className="border-0"
            placeholder="Enter the zip code of where you want to ship a package"
            aria-label="Zipcode"
            value={zip}
            onChange={handleZipChange}
            required
            isInvalid={!!validationError}
          />
          <InputGroup.Append className="border-0">
            <SubmitBtn
              isSubmitting={validating || fetching}
              type="submit"
              className="btn-slant border-0"
            >
              Search
            </SubmitBtn>
          </InputGroup.Append>
          {!!validationError && (
            <Form.Control.Feedback as="span" type="invalid">
              {validationError}
            </Form.Control.Feedback>
          )}
        </InputGroup>
      </Form>
      {hasSearched && (hasService ? ValidZipMessage : InvalidZipMessage)}
    </div>
  );
};

export default SearchServiceArea;
